import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, StarIcon, UsersIcon, CheckCircleIcon, ArrowRightIcon, ChevronDownIcon, ZapIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'Why should I advertise on Yelp instead of Google?',
  answer: 'Yelp users have exceptionally high purchase intent. When someone searches for a plumber or roofer on Yelp, they are usually looking to hire someone that same day. While Google is great for broad searches, Yelp is the bottom of the funnel for local services.'
}, {
  question: 'What does it mean to be a Yelp Advertising Premier Partner?',
  answer: "As a Yelp Advertising Premier Partner, we have direct access to Yelp representatives, priority support, and exclusive insights into bidding strategies and platform updates that regular advertisers don't get. Premier is Yelp's top partner tier, reserved for agencies with proven performance."
}, {
  question: 'Do I need a large budget to start?',
  answer: 'No. We work with businesses of all sizes to determine a starting budget that makes sense for your specific industry and local market competition. We focus on maximizing ROI, not just spending money.'
}, {
  question: 'How do you track if the ads are actually working?',
  answer: "We implement full-funnel tracking. We don't just track clicks or impressions; we track actual phone calls, messages, and form fills generated from the ads, tying them directly back to your revenue."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-392">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-393">
        <span className="text-lg font-semibold text-white" data-id="element-394">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-395">
          <ChevronDownIcon className="w-5 h-5" data-id="element-396" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-397">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-398">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-399">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function AdCampaignsPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-400">
      <SEOHead title="Yelp Advertising Premier Partner | Ad Campaign Management | Viridia Analytics Phoenix" description="Expert Yelp Ads campaign management for Phoenix local businesses. As a Yelp Advertising Premier Partner, we maximize your ad spend and drive qualified leads." canonical="/ad-campaigns" keywords="Yelp Ads management Phoenix, Yelp Ads partner agency, local business advertising, ad campaign optimization, lead generation Phoenix" faq={faqs} data-id="element-401" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-402">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-403">
          <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-red-500/20 blur-[120px] animate-pulse-slow" data-id="element-404"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-405">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-406">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-407">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-408">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-semibold tracking-wide mb-6" data-id="element-409">
                  PAID ADVERTISING
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-410">
                  Your Yelp Ads{' '}
                  <span className="text-red-500" data-id="element-411">Partner Agency</span>
                </h1>
                <p className="text-lg text-gray-400 mb-4 leading-relaxed" data-id="element-412">
                  Leverage our insider expertise from former Yelp employees to
                  drive qualified leads. We understand CPC optimization,
                  competitive bidding, and seasonality trends that maximize ROI
                  on Yelp for local service businesses.
                </p>
                <p className="text-lg text-viridia font-medium mb-8 leading-relaxed" data-id="element-413">
                  In the last 30 days, our clients generated 380+ Yelp leads and
                  booked 57 AI estimates across two campaigns.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-414">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-415">
                    Book a Free Ad Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-416">
                    View Pricing
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration */}
            <div className="relative h-[400px] lg:h-[500px] hidden md:block" data-id="element-417">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="absolute inset-0 flex items-center justify-center" data-id="element-418">
                {/* Central Yelp Partner Badge */}
                <motion.div className="relative z-30 flex flex-col items-center" animate={{
                y: [0, -15, 0]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-419">
                  <img src="https://cdn.magicpatterns.com/uploads/rbq8W356ty5oybkr7qHyKz/yelp-premiere-partner-1.png" alt="Yelp Advertising Premier Partner" className="w-80 h-80 object-contain drop-shadow-[0_0_40px_rgba(239,68,68,0.4)]" data-id="element-420" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORMS SECTION */}
      <section className="py-24 bg-viridia-light relative" data-id="element-421">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-422">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-423">
            <div data-id="element-424">
              <ScrollReveal data-id="element-425">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-426">
                  PLATFORM EXPERTISE
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-427">
                  Why Yelp Ads?
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-428">
                  Yelp is where high-intent customers go when they are ready to
                  hire a local service professional. We don't just set and
                  forget. We actively manage your campaigns using insider
                  knowledge to maximize your return on ad spend.
                </p>
                <div className="space-y-6" data-id="element-429">
                  {['TFP Landscaping: 242 leads, 36.4k impressions in 30 days', 'Finish Coat Painting: 138 leads, 33.2k impressions in 30 days', 'Optimize Cost-Per-Click (CPC) to stretch your budget', 'Leverage our Partner Agency priority support'].map((feature, index) => <div key={index} className="flex items-start" data-id="element-430">
                      <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0 mt-0.5" data-id="element-431" />
                      <span className="text-gray-700 font-medium text-lg" data-id="element-432">
                        {feature}
                      </span>
                    </div>)}
                </div>
              </ScrollReveal>
            </div>

            <div className="relative" data-id="element-433">
              <ScrollReveal direction="left" data-id="element-434">
                <div className="bg-white rounded-2xl p-10 shadow-xl border-t-4 border-red-500 relative overflow-hidden" data-id="element-435">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full -z-10" data-id="element-436"></div>
                  <div className="w-16 h-16 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 mb-8" data-id="element-437">
                    <StarIcon className="w-8 h-8 fill-current" data-id="element-438" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-439">
                    Targeted Local Advertising
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg mb-8" data-id="element-440">
                    Reach customers already looking for businesses like yours on
                    Yelp. Our team includes former Yelp insiders who know
                    exactly how to maximize your ad spend on the platform.
                  </p>
                  <Link to="/contact" className="inline-flex items-center text-red-500 font-bold hover:text-red-600 transition-colors text-lg" data-id="element-441">
                    Start Dominating Yelp{' '}
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-442" />
                  </Link>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON SECTION */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-443">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-444">
          <ScrollReveal data-id="element-445">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-446">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-447">
                Ads Are Only Half the Equation
              </h2>
              <p className="text-gray-400" data-id="element-448">
                Generating leads doesn't matter if you don't close them. Here is
                how we differ from a standard ad agency.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-449">
            <ScrollReveal data-id="element-450">
              <div className="bg-viridia-surface border border-red-900/30 rounded-2xl p-8 relative overflow-hidden" data-id="element-451">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-3xl" data-id="element-452"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-453">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-454">
                    <XCircleIcon className="w-6 h-6 text-red-500" data-id="element-455" />
                  </div>
                  <div data-id="element-456">
                    <h3 className="text-xl font-bold text-white" data-id="element-457">
                      Agency That Only Runs Ads
                    </h3>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-458">
                  <li className="flex items-start gap-3" data-id="element-459">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-460" />
                    <span className="text-gray-300" data-id="element-461">
                      Runs ads and sends you a monthly report
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-462">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-463" />
                    <span className="text-gray-300" data-id="element-464">
                      Leads go to voicemail while you're working
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-465">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-466" />
                    <span className="text-gray-300" data-id="element-467">
                      No follow-up system in place
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-468">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-469" />
                    <span className="text-gray-300" data-id="element-470">
                      You pay for leads that never convert
                    </span>
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-471">
              <div className="bg-viridia-surface border border-viridia/30 rounded-2xl p-8 relative overflow-hidden" data-id="element-472">
                <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/10 rounded-full blur-3xl" data-id="element-473"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-474">
                  <div className="w-12 h-12 rounded-full bg-viridia/20 flex items-center justify-center" data-id="element-475">
                    <ZapIcon className="w-6 h-6 text-viridia" data-id="element-476" />
                  </div>
                  <div data-id="element-477">
                    <h3 className="text-xl font-bold text-white" data-id="element-478">
                      Viridia: Ads + AI + Follow-Up
                    </h3>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-479">
                  <li className="flex items-start gap-3" data-id="element-480">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-481" />
                    <span className="text-gray-300" data-id="element-482">
                      Runs targeted local ads
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-483">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-484" />
                    <span className="text-gray-300" data-id="element-485">
                      AI responds to every lead instantly
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-486">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-487" />
                    <span className="text-gray-300" data-id="element-488">
                      Estimates are booked automatically
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-489">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-490" />
                    <span className="text-gray-300" data-id="element-491">
                      CRM tracks everything so you see real revenue
                    </span>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-492">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-493">
          <ScrollReveal data-id="element-494">
            <div className="mb-16" data-id="element-495">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-496">
                What You Get
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl" data-id="element-497">
                A comprehensive approach to paid advertising that covers every
                angle of your campaigns.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-498">
            {[{
            title: 'Campaign Strategy & Setup',
            desc: 'Custom-built campaigns tailored to your specific business goals and target audience to drive booked jobs.'
          }, {
            title: 'Ad Creative & Copywriting',
            desc: 'Compelling ad copy and visuals designed to capture attention and drive high-intent clicks.'
          }, {
            title: 'Keyword Research & Targeting',
            desc: 'In-depth research to find the most profitable search terms for your local services.'
          }, {
            title: 'Bid Management & Optimization',
            desc: 'Continuous monitoring and adjustment of bids to maximize your return on ad spend and lower cost per lead.'
          }, {
            title: 'A/B Testing & Performance Tuning',
            desc: 'Rigorous testing of different ad variations to find the highest converting combinations for your market.'
          }, {
            title: 'Monthly Reporting & Strategy Calls',
            desc: 'Transparent reporting on key metrics and regular calls to discuss strategy and actual revenue results.'
          }, {
            title: 'Landing Page Recommendations',
            desc: 'Expert advice on optimizing your website to convert the traffic we send into booked estimates.'
          }, {
            title: 'Competitor Analysis',
            desc: "Ongoing monitoring of your competitors' ad strategies to keep you one step ahead in your local area."
          }].map((feature, index) => <ScrollReveal key={index} delay={index * 0.05} data-id="element-499">
                <div className="flex items-start" data-id="element-500">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mt-1 flex-shrink-0 mr-4" data-id="element-501" />
                  <div data-id="element-502">
                    <h4 className="text-lg font-semibold text-white mb-1" data-id="element-503">
                      {feature.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-504">{feature.desc}</p>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-505">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-506">
          <ScrollReveal data-id="element-507">
            <div className="text-center mb-16" data-id="element-508">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-509">
                Frequently Asked Questions
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-510">
            <div className="bg-viridia-surface rounded-2xl p-6 md:p-8 border border-gray-800" data-id="element-511">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-512" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-513">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-514"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-515">
          <ScrollReveal data-id="element-516">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-517">
              See how your Yelp Ads could perform with AI follow-up behind them
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-518">
              Book a free strategy call and we'll audit your current campaigns -
              or build a plan from scratch.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-519">
              Book Your Free Ad Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-520" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}